import React, { useCallback, useEffect, useState } from "react";
import { api, errorMessage, downloadFile } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AUDIT_ACTIONS } from "@/lib/constants";
import { formatDateTime, todayIso } from "@/lib/format";
import { toast } from "sonner";
import { Download, X } from "lucide-react";

export default function AdminAuditLogPage() {
  const [data, setData] = useState(null);
  const [action, setAction] = useState("alle");
  const [userId, setUserId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = useCallback(() => {
    api.get("/admin/audit-logs", { params: { action: action !== "alle" ? action : undefined, user_id: userId || undefined, date_from: from || undefined, date_to: to || undefined, limit: 300 } }).then((r) => setData(r.data)).catch((e) => toast.error(errorMessage(e)));
  }, [action, userId, from, to]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try { await downloadFile("/admin/audit-logs/export.csv", `revisjonslogg_${todayIso()}.csv`, { action: action !== "alle" ? action : undefined, user_id: userId || undefined }); toast.success("Eksport lastet ned"); } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Revisjonslogg" description="Alle hendelser i systemet – kun metadata (hvem, hva, når). Aldri fakturainnhold." actions={<Button variant="outline" onClick={exportCsv} data-testid="admin-auditlog-export-button"><Download className="h-4 w-4 mr-2" /> Eksporter CSV</Button>} />
      <div className="rounded-xl border bg-card p-3 grid gap-3 md:grid-cols-[1fr_1fr_auto_auto_auto]">
        <Select value={action} onValueChange={setAction}><SelectTrigger data-testid="admin-audit-action-select"><SelectValue placeholder="Handling" /></SelectTrigger><SelectContent className="bg-card max-h-72"><SelectItem value="alle">Alle handlinger</SelectItem>{(data?.actions || []).map((a) => <SelectItem key={a} value={a}>{AUDIT_ACTIONS[a] || a}</SelectItem>)}</SelectContent></Select>
        <Input placeholder="Bruker-ID" value={userId} onChange={(e) => setUserId(e.target.value)} data-testid="admin-audit-user-input" />
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="Fra" data-testid="admin-audit-from" />
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="Til" data-testid="admin-audit-to" />
        <Button variant="ghost" onClick={() => { setAction("alle"); setUserId(""); setFrom(""); setTo(""); }}><X className="h-4 w-4 mr-1" /> Nullstill</Button>
      </div>
      {!data ? <Skeleton className="h-96" /> : (
        <div className="rounded-xl border bg-card overflow-hidden">
          <Table data-testid="admin-audit-table">
            <TableHeader><TableRow><TableHead>Tidspunkt</TableHead><TableHead>Handling</TableHead><TableHead className="hidden md:table-cell">Bruker-ID</TableHead><TableHead className="hidden lg:table-cell">Objekt</TableHead><TableHead className="hidden lg:table-cell">IP</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.items.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">Ingen hendelser</TableCell></TableRow> : data.items.map((a) => (
                <TableRow key={a.id} data-testid={`audit-row-${a.id}`}>
                  <TableCell className="text-sm tnum whitespace-nowrap">{formatDateTime(a.created_at)}</TableCell>
                  <TableCell className="text-sm"><span className="font-medium">{AUDIT_ACTIONS[a.action] || a.action}</span>{a.actor_role === "admin" ? <span className="ml-2 rounded-full bg-admin px-1.5 py-0.5 text-[10px]">admin</span> : null}</TableCell>
                  <TableCell className="hidden md:table-cell text-xs text-muted-foreground font-mono">{a.user_id ? <button type="button" className="hover:underline" onClick={() => setUserId(a.user_id)}>{a.user_id.slice(0, 8)}…</button> : "–"}</TableCell>
                  <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">{a.target_type || "–"}{a.target_id ? ` · ${a.target_id.slice(0, 8)}…` : ""}</TableCell>
                  <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">{a.ip || "–"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
